import * as React from 'react';
import Icon from '../../../widgets/Icons';
import { Divider } from '@rtkwlf/ui';
import { StatusSection } from '../../../types';
import styled from 'styled-components';
import './StatusSectionComponent.scss';

type Props = {
  title: string;
  // if set, will replace `title` as the rendering content
  titleNode?: React.ReactNode;
  status?: 'success' | 'fail';
  children: StatusSection[];
  depth: number;
};

const maxFontSize = 20;
const fontSizeChange = 3;

export const StatusSectionContainer = styled.div`
  display: block;
  margin-bottom: 20px;
`;

export const StatusSectionComponent: React.FunctionComponent<Props> = (
  props,
) => {
  const { children, title, titleNode, status, depth } = props;
  const hasChildren = React.useMemo(
    (): boolean => children && children?.length > 0,
    [children],
  );

  const childrenNodes = React.useMemo(
    () =>
      children.map((c) => (
        <StatusSectionComponent
          key={c.title}
          data-cy={`section-${c.title}`}
          title={c.title}
          titleNode={c.titleNode}
          status={c.status}
          children={c.children || []}
          depth={depth + 1}
        />
      )),
    [children, depth],
  );

  return (
    <StatusSectionContainer
      data-cy={`section-${title}`}
      className={`ml-${depth + 1}`}
    >
      <p
        style={{
          fontSize:
            depth <= 0 ? maxFontSize : maxFontSize - (depth + fontSizeChange),
          fontWeight: 'bold',
        }}
        className="font-heading text-lg font-normal mb-3"
        data-cy={`title-${title}`}
      >
        {status === 'success' && (
          <Icon.CheckCircle
            className="status-checklist-checkmark-complete"
            data-cy={`status-${status}-${title}`}
          />
        )}
        {status === 'fail' && (
          <Icon.XCircle
            className="status-checklist-checkmark-incomplete"
            data-cy={`status-${status}-${title}`}
          />
        )}
        {titleNode ?? title}
      </p>
      {hasChildren && (
        <>
          <Divider className={'mb-3'} />
          {childrenNodes}
        </>
      )}
    </StatusSectionContainer>
  );
};
